import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  ApiError,
  type AppearanceFieldErrors,
  type AriaPersona,
  assistantApi,
} from "./api.ts";
import { resetSharedAssistantConfig, sharedAssistantConfig } from "./appearance-context.tsx";
import { MODEL_FALLBACK, MODEL_PRIMARY } from "./ariaOptions.ts";
import { mergeOverDefaults } from "./savedConfig.mjs";

// Mirrors tools/aria_persona_config.py's DEFAULT_ARIA_PERSONA exactly --
// same field names, same values. Kept as a frozen literal (not derived from
// a server round-trip) so the studio has something correct to render before
// the first GET resolves.
const DEFAULT_ARIA_PERSONA: AriaPersona = Object.freeze({
  version: 1,
  persona: Object.freeze({
    name: "", role: "Support specialist", pronouns: "they/them",
    signature: "", mark: "initials", greet_style: "warm",
    emoji: "never", humour: "light", self_disclose: "on-ask",
  }),
  tone: Object.freeze({
    preset: "concise", warmth: 55, formality: 55, verbosity: 35,
    directness: 80, energy: 40, tech: 45,
    reading: "grade8", sentence: "short", spelling: "us",
  }),
  language: Object.freeze({
    primary: "en", supported: Object.freeze(["en"]) as unknown as string[], detect: true,
    fallback: "en", translate_transcripts: true, dates: "locale",
    units: "imperial", profanity: "mirror-neutral",
  }),
  behavior: Object.freeze({
    proactivity: "balanced", act_policy: "ask-then-act", clarify_max: 2,
    confidence_floor: 72, max_turns: 6, typing_delay_ms: 600,
    followup: "once", closeout: "summary", summarize_for_operators: true,
  }),
  knowledge: Object.freeze({
    sources: Object.freeze(["help centre", "policies", "order data", "past resolutions"]) as unknown as string[],
    depth: 6, freshness_days: 90, semantic_mix: 65,
    cite: "always", unknown: "admit-and-route", strictness: 85,
  }),
  tools: Object.freeze({
    order_lookup: { on: true, approval: "none" },
    returns: { on: true, approval: "none" },
    refund: { on: true, approval: "over-cap" },
    sub_change: { on: true, approval: "always" },
    address: { on: true, approval: "none" },
    booking: { on: false, approval: "none" },
    invoice: { on: true, approval: "none" },
    password: { on: true, approval: "none" },
    ticket: { on: true, approval: "none" },
    callback: { on: true, approval: "none" },
    escalate: { on: true, approval: "none" },
    kb_write: { on: false, approval: "always" },
  }) as unknown as AriaPersona["tools"],
  guardrails: Object.freeze({
    banned_topics: Object.freeze(["Medical advice", "Legal advice", "Competitor comparisons"]) as unknown as string[],
    jailbreak_resistance: "high", pii: "redact", disclaimers: true,
    record_notice: true, markdown: "links-lists",
    refund_ceiling_usd: 250, discount_ceiling_pct: 15,
  }),
  escalation: Object.freeze({
    triggers: Object.freeze(["low confidence", "negative sentiment", "explicit ask", "refund over cap"]) as unknown as string[],
    route: "skill-based", sla_minutes: 5, after_hours: "ticket",
    warm_transfer: true, handoff_line: "Bringing in a teammate now — one moment.",
  }),
  memory: Object.freeze({
    cross_session: true, window_days: 180, identity_confidence: 80,
    remember: Object.freeze(["name", "order history", "preferences", "language"]) as unknown as string[],
    forget_on_request: true,
  }),
  voice: Object.freeze({
    tts_voice: "ember", rate_pct: 100, pitch_semitones: 0, barge_in: true,
    silence_timeout_s: 6, dtmf: true, transcribe: true, hold_audio: "ambient",
    greeting: "Thanks for calling. How can I help?",
  }),
  model: Object.freeze({
    primary: "sonnet", fallback: "opus", temperature_pct: 30,
    max_tokens: 700, stream: true, cache: true,
    latency_target_ms: 1200, monthly_ceiling_usd: 400,
  }),
  rollout: Object.freeze({
    canary_pct: 15, eval_gate_pct: 90, ab_test: false, variant: "concierge",
    version: "v1", notes: "", last_eval_pass_pct: null,
  }),
});

export function defaultAriaPersona(): AriaPersona {
  // Deep-ish clone: every nested object below is re-spread so a caller
  // mutating a draft never reaches back into the frozen literal above.
  return {
    version: 1,
    persona: { ...DEFAULT_ARIA_PERSONA.persona },
    tone: { ...DEFAULT_ARIA_PERSONA.tone },
    language: { ...DEFAULT_ARIA_PERSONA.language, supported: [...DEFAULT_ARIA_PERSONA.language.supported] },
    behavior: { ...DEFAULT_ARIA_PERSONA.behavior },
    knowledge: { ...DEFAULT_ARIA_PERSONA.knowledge, sources: [...DEFAULT_ARIA_PERSONA.knowledge.sources] },
    tools: Object.fromEntries(
      Object.entries(DEFAULT_ARIA_PERSONA.tools).map(([k, v]) => [k, { ...v }]),
    ) as AriaPersona["tools"],
    guardrails: { ...DEFAULT_ARIA_PERSONA.guardrails, banned_topics: [...DEFAULT_ARIA_PERSONA.guardrails.banned_topics] },
    escalation: { ...DEFAULT_ARIA_PERSONA.escalation, triggers: [...DEFAULT_ARIA_PERSONA.escalation.triggers] },
    memory: { ...DEFAULT_ARIA_PERSONA.memory, remember: [...DEFAULT_ARIA_PERSONA.memory.remember] },
    voice: { ...DEFAULT_ARIA_PERSONA.voice },
    model: { ...DEFAULT_ARIA_PERSONA.model },
    rollout: { ...DEFAULT_ARIA_PERSONA.rollout },
  };
}

export type AriaPersonaStatus = "loading" | "ready" | "saving" | "saved" | "error";

interface AriaPersonaContextValue {
  persona: AriaPersona;
  status: AriaPersonaStatus;
  fieldErrors: AppearanceFieldErrors;
  publishedVersion: number;
  save: (persona: AriaPersona) => Promise<AriaPersona>;
  reload: () => Promise<void>;
}

const AriaPersonaContext = createContext<AriaPersonaContextValue | null>(null);

// The model tiers were renamed on 2026-08-19 (they used to name the retired
// provider's model sizes). A persona saved before then still holds an old
// value, which no longer matches any option in the picker: the select would
// render blank, and saving without touching it would fail server-side
// validation with an error the page gives the operator no way to act on.
// Falling back to the default keeps the control showing a real, saveable
// choice. Deliberately a no-op for any value the picker knows -- this only
// catches values that are already unselectable.
function knownOr<T extends string>(
  value: unknown,
  options: readonly { value: string }[],
  fallback: T,
): T {
  return options.some((o) => o.value === value) ? (value as T) : fallback;
}

/* Mirrors PROACTIVITY_LEGACY in tools/aria_persona_config.py. The server
   migrates a stored low/medium/high on read, but the app and the API deploy
   separately: until that server ships, a tenant saved as "medium" would open
   this page to a segmented control with nothing selected, and saving from
   there would look like a deliberate choice of whatever they clicked next. */
const PROACTIVITY_LEGACY: Record<string, AriaPersona["behavior"]["proactivity"]> = {
  low: "reactive",
  medium: "balanced",
  high: "anticipatory",
};

function normalizedPersona(response: Awaited<ReturnType<typeof assistantApi.get>>): AriaPersona {
  const config = response.result?.config;
  if (!response.ok || !config || config.aria_persona?.version !== 1) {
    throw new Error(response.error || "Your assistant's persona settings were unavailable.");
  }
  // Merged over the defaults so a server a release behind this bundle cannot
  // hand a section straight to render with a field missing.
  const persona = mergeOverDefaults(defaultAriaPersona(), config.aria_persona);
  return {
    ...persona,
    behavior: {
      ...persona.behavior,
      proactivity: PROACTIVITY_LEGACY[persona.behavior.proactivity] ?? persona.behavior.proactivity,
    },
    model: {
      ...persona.model,
      primary: knownOr(persona.model.primary, MODEL_PRIMARY, DEFAULT_ARIA_PERSONA.model.primary),
      fallback: knownOr(persona.model.fallback, MODEL_FALLBACK, DEFAULT_ARIA_PERSONA.model.fallback),
    },
  };
}

function apiFieldErrors(error: unknown): AppearanceFieldErrors {
  if (!(error instanceof ApiError) || typeof error.body !== "object" || error.body === null) {
    return {};
  }
  const body = error.body as Record<string, unknown>;
  const raw = body.field_errors;
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) return {};

  return Object.fromEntries(
    Object.entries(raw).filter(
      (entry): entry is [string, string] => typeof entry[1] === "string",
    ),
  );
}

export function AriaPersonaProvider({ children }: { children: ReactNode }) {
  const [persona, setPersona] = useState(defaultAriaPersona);
  const [status, setStatus] = useState<AriaPersonaStatus>("loading");
  const [fieldErrors, setFieldErrors] = useState<AppearanceFieldErrors>({});
  const [publishedVersion, setPublishedVersion] = useState(0);
  const generation = useRef(0);
  const controller = useRef<AbortController | null>(null);

  const reload = useCallback(async () => {
    controller.current?.abort();
    const requestController = new AbortController();
    controller.current = requestController;
    const requestGeneration = ++generation.current;
    setStatus("loading");
    setFieldErrors({});

    try {
      // No signal: the shared read is joined by other mounted surfaces and one
      // unmount must not cancel it. Staleness is handled two lines down by the
      // generation guard, which is what the coalescer's header relies on.
      const next = normalizedPersona(await sharedAssistantConfig());
      if (requestController.signal.aborted || generation.current !== requestGeneration) return;
      setPersona(next);
      setStatus("ready");
    } catch {
      if (requestController.signal.aborted || generation.current !== requestGeneration) return;
      // Defaults or the last known-good state remain intact on every failure.
      setStatus("error");
    }
  }, []);

  useEffect(() => {
    void reload();
    return () => {
      controller.current?.abort();
      generation.current += 1;
    };
  }, [reload]);

  // Publish only broadcasts the new persona into this context on a
  // successful save -- never on every keystroke a draft editor makes.
  // Same contract as white-label propagation: an unpublished draft must
  // never repaint what the runtime is actually using.
  const save = useCallback(async (nextPersona: AriaPersona) => {
    controller.current?.abort();
    const requestController = new AbortController();
    controller.current = requestController;
    const requestGeneration = ++generation.current;
    setStatus("saving");
    setFieldErrors({});

    try {
      const written = await assistantApi.update(
        { aria_persona: nextPersona },
        requestController.signal,
      );
      // WRITE BARRIER -- see resetSharedAssistantConfig's header. A GET that was
      // in flight when this save landed carries the pre-save persona, and any
      // reload() afterwards would join it rather than ask again.
      resetSharedAssistantConfig();
      const saved = normalizedPersona(written);
      if (requestController.signal.aborted || generation.current !== requestGeneration) {
        return saved;
      }
      setPersona(saved);
      setPublishedVersion((v) => v + 1);
      setStatus("saved");
      return saved;
    } catch (error) {
      if (!requestController.signal.aborted && generation.current === requestGeneration) {
        setFieldErrors(apiFieldErrors(error));
        setStatus("error");
      }
      throw error;
    }
  }, []);

  const value = useMemo<AriaPersonaContextValue>(() => ({
    persona,
    status,
    fieldErrors,
    publishedVersion,
    save,
    reload,
  }), [persona, status, fieldErrors, publishedVersion, save, reload]);

  return (
    <AriaPersonaContext.Provider value={value}>
      {children}
    </AriaPersonaContext.Provider>
  );
}

export function useAriaPersona(): AriaPersonaContextValue {
  const value = useContext(AriaPersonaContext);
  if (!value) {
    throw new Error("useAriaPersona must be used inside <AriaPersonaProvider>");
  }
  return value;
}
